import DatePicker from "@/components/date-picker";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import {
  AsyncAutocompleteSelect,
  type Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { InputField } from "@/components/input-field/InputField";
import { TextArea } from "@/components/text-area/TextArea";
import { toaster } from "@/components/ui/toaster";
import { useRoles } from "@/hooks/useRoles";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { getDepositos, getProcedencias } from "@/services/aux-entities.service";
import { getFertilizantes } from "@/services/products.service";
import { SelectComponent } from "@/components/select/Select";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { isEmpty, parseTonInputToNumber } from "@/utils/query-string";
import { onlyNumbers } from "@/utils/reg-expressions";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "@/pages/grain-requests/sell-orders/schema/handler-functions";
import {
  Button,
  Card,
  Field,
  Flex,
  Grid,
  GridItem,
  Text,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { SubmitHandler, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { createFertSlotRequestHandler } from "./schema/handler-functions";
import { RequestProductElement } from "./components/RequestProductElement";
import { FertSlotRequestCreationForm } from "../schema/fert-slot-requests.schema";
import { CustomCreatableSelect } from "@/components/select/CustomCreatableSelect";
import { PermissionCode } from "@/constants/permissions";
import { ActionCode } from "@/constants/action-codes";
import { useBlockedActionGuard } from "@/hooks/useBlockedActionGuard";

type SelectItem = { label: string; value: string };

export const FertSlotRequestCreationPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  useEffect(() => setIsActive(true), []);
  useBlockedActionGuard(
    PermissionCode.FERT_SLOTS_REQ,
    ActionCode.CREATE_REQUEST,
    AppRoutes.FERT_SLOTS_REQ,
    isActive,
  );

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FertSlotRequestCreationForm>({
    defaultValues: {
      account: null,
      destino: "",
      kmDistancia: "",
      contacto: "",
      nombreContacto: "",
      observaciones: "",
      idDeposito: [],
      productos: [{ idProducto: [], cantidad: "" }],
    },
  });

  const watchedValues = useWatch({ control });
  const watchedAccount = useWatch({ control, name: "account" });
  const watchedProductos = useWatch({ control, name: "productos" });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "productos",
  });

  const [loadingCreate, setLoadingCreate] = useState(false);

  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadAccountOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions: (v) => getOptionsHandler({ inputValue: v }),
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      timeoutRef,
    });
  };

  const [procedencias, setProcedencias] = useState<any[]>([]);
  const [loadingProcedencias, setLoadingProcedencias] = useState(false);

  const fetchProcedencias = async (cuenta?: string) => {
    if (!cuenta) return;
    const nro = Number(cuenta);
    if (!Number.isFinite(nro)) return;

    setLoadingProcedencias(true);
    try {
      const response = await getProcedencias({ cuenta: nro });
      setProcedencias(response ?? []);
    } finally {
      setLoadingProcedencias(false);
    }
  };

  useEffect(() => {
    if (!watchedAccount?.value) return;

    if (isActive) {
      setValue("destino", "");
      setProcedencias([]);
      void fetchProcedencias(String(watchedAccount.value));
    }
  }, [watchedAccount, isActive]);

  const procedenciaItems: SelectItem[] = useMemo(() => {
    return (
      procedencias?.map((p: any) => ({
        label: p?.Nombre ?? p?.nombre ?? "-",
        value: String(p?.Id ?? p?.id ?? ""),
      })) ?? []
    );
  }, [procedencias]);

  const [depositos, setDepositos] = useState<{ id: number; nombre: string }[]>(
    [],
  );
  const [loadingDepositos, setLoadingDepositos] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;

    const run = async () => {
      setLoadingDepositos(true);
      try {
        const response = await getDepositos();
        if (cancelled) return;
        setDepositos(response ?? []);
      } catch (error) {
        if (cancelled) return;

        const handled = await handlePermissionError(
          error,
          navigate,
          fetchPermissions,
          AppRoutes.FERT_SLOTS_REQ,
        );

        if (!handled) {
          toaster.create({
            type: "error",
            title: getAxiosErrorMessage(
              error,
              "No se pudieron cargar los depósitos.",
            ),
          });
        }
        setDepositos([]);
      } finally {
        if (!cancelled) setLoadingDepositos(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [isActive]);

  const depositoItems: SelectItem[] = useMemo(() => {
    return (depositos ?? [])
      .filter((d) => Number.isFinite(Number(d?.id)))
      .map((d) => ({ label: String(d?.nombre ?? "-"), value: String(d?.id) }));
  }, [depositos]);

  const [fertilizantes, setFertilizantes] = useState<
    { id: number; nombre: string }[]
  >([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    const run = async () => {
      setLoadingProducts(true);
      try {
        const response = await getFertilizantes({ activo: true });
        const mapped = (response ?? []).map((p: any) => ({
          id: Number(p?.id),
          nombre: String(p?.nombre ?? ""),
        }));
        setFertilizantes(mapped);
      } catch (error) {
        const handled = await handlePermissionError(
          error,
          navigate,
          fetchPermissions,
          AppRoutes.FERT_SLOTS_REQ,
        );

        if (!handled) {
          toaster.create({
            type: "error",
            title: getAxiosErrorMessage(
              error,
              "No se pudieron cargar los productos.",
            ),
          });
        }
        setFertilizantes([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    void run();
  }, [isActive]);

  const selectedProductIds = useMemo(() => {
    const ids = (watchedProductos ?? [])
      .map((p: any) =>
        Array.isArray(p?.idProducto) ? p.idProducto?.[0] : undefined,
      )
      .map((v: any) => String(v ?? "").trim())
      .filter(Boolean);
    return ids;
  }, [watchedProductos]);

  const hasDuplicatedProducts = useMemo(() => {
    const s = new Set<string>();
    for (const id of selectedProductIds) {
      if (s.has(id)) return true;
      s.add(id);
    }
    return false;
  }, [selectedProductIds]);

  const hasIncompleteProducts = useMemo(() => {
    const list = (watchedProductos ?? []) as any[];
    if (!list.length) return true;

    return list.some((p) => {
      const id = Array.isArray(p?.idProducto) ? p.idProducto?.[0] : undefined;
      const cantidad = parseTonInputToNumber(p?.cantidad) ?? 0;
      return !id || cantidad <= 0;
    });
  }, [watchedProductos]);

  const productItems: Array<SelectItem & { disabled?: boolean }> =
    useMemo(() => {
      const selectedSet = new Set(selectedProductIds);
      return (fertilizantes ?? [])
        .filter((f) => Number.isFinite(Number(f?.id)))
        .map((f) => ({
          label: String(f?.nombre ?? "-"),
          value: String(f?.id),
          disabled: selectedSet.has(String(f?.id)),
        }));
    }, [fertilizantes, selectedProductIds]);

  const hasNoFilledValues = useMemo(() => {
    const {
      account,
      fechaEntrega,
      destino,
      kmDistancia,
      contacto,
      idDeposito,
      productos,
    } = watchedValues || {};

    if (isEmpty(account)) return true;
    if (isEmpty(fechaEntrega)) return true;
    if (isEmpty(destino)) return true;
    if (isEmpty(kmDistancia)) return true;
    if (isEmpty(contacto)) return true;
    if (isEmpty(idDeposito)) return true;

    if ((productos ?? []).length === 0) return true;
    if (hasIncompleteProducts) return true;
    if (hasDuplicatedProducts) return true;

    return false;
  }, [watchedValues, hasDuplicatedProducts, hasIncompleteProducts]);

  const handleCancel = () => {
    navigate(AppRoutes.FERT_SLOTS_REQ);
  };

  const onSubmit: SubmitHandler<FertSlotRequestCreationForm> = async (values) => {
    await createFertSlotRequestHandler({
      values,
      setLoadingCreate,
      navigate,
      fetchPermissions,
      procedenciaItems,
    });
  };

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Agregar cupos de fertilizantes"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingCreate}
        loading={loadingCreate}
      />

      <Flex
        flexDir="column"
        gap="24px"
        marginTop="30px"
        overflowY="auto"
        minH={0}
        flex="1"
        paddingRight="8px"
        paddingBottom="8px"
      >
        <Card.Root maxW="full" borderRadius="8px">
          <Card.Body
            paddingX="48px"
            paddingY="30px"
            borderRadius="8px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Grid templateColumns="repeat(3, 1fr)" gap={6}>
              <GridItem colSpan={1}>
                <Field.Root invalid={!!(errors as any)?.account} gap="14px">
                  <Field.Label>
                    <Text fontSize="16px">Cuenta</Text>
                  </Field.Label>
                  <AsyncAutocompleteSelect
                    control={control as any}
                    loadOptions={loadAccountOptions}
                    name="account"
                    placeholder="Seleccionar"
                    isLoading={loadingAccounts}
                    disabled={loadingCreate}
                  />
                  <Field.ErrorText>
                    {(errors as any)?.account?.message}
                  </Field.ErrorText>
                </Field.Root>
              </GridItem>

              <GridItem colSpan={1} paddingY="8px">
                <DatePicker
                  control={control as any}
                  name="fechaEntrega"
                  labelText="Fecha de entrega"
                  placeholder="Seleccionar"
                  minDate={dayjs()}
                  disablePortal={false}
                  minHeight="40px"
                  disabled={loadingCreate}
                />
              </GridItem>

              <GridItem colSpan={1} paddingY="8px">
                <CustomCreatableSelect
                  control={control}
                  name="destino"
                  labelText="Destino (Establecimiento)"
                  placeholder="Seleccionar"
                  items={procedenciaItems}
                  loading={loadingProcedencias}
                  disabled={
                    !watchedAccount || loadingProcedencias || loadingCreate
                  }
                  required
                />
              </GridItem>

              <GridItem colSpan={1} paddingY="8px">
                <InputField
                  inputName="kmDistancia"
                  labelText="Km a campo"
                  placeholder="Ingresar"
                  control={control as any}
                  allowedPattern={onlyNumbers}
                  minHeight="40px"
                  disabled={loadingCreate}
                  maxLength={12}
                />
              </GridItem>

              <GridItem colSpan={1} paddingY="8px">
                <InputField
                  inputName="contacto"
                  labelText="Contacto (Teléfono)"
                  placeholder="Ingresar"
                  control={control as any}
                  allowedPattern={onlyNumbers}
                  minHeight="40px"
                  disabled={loadingCreate}
                  maxLength={14}
                />
              </GridItem>

              <GridItem colSpan={1} paddingY="8px">
                <InputField
                  inputName="nombreContacto"
                  labelText="Contacto (Nombre)"
                  placeholder="Ingresar"
                  control={control as any}
                  minHeight="40px"
                  disabled={loadingCreate}
                  maxLength={100}
                />
              </GridItem>
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root maxW="full" borderRadius="8px">
          <Card.Body
            paddingX="48px"
            paddingY="30px"
            borderRadius="8px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Grid templateColumns="repeat(3, 1fr)" gap={6}>
              <GridItem colSpan={1}>
                <SelectComponent
                  fullWidth
                  name="idDeposito"
                  control={control as any}
                  labelText="Depósito"
                  placeholder="Seleccionar"
                  items={depositoItems}
                  loading={loadingDepositos}
                  disabled={loadingDepositos || loadingCreate}
                  minHeight="40px"
                  required
                />
              </GridItem>
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root maxW="full" borderRadius="8px">
          <Card.Body
            paddingX="48px"
            paddingY="30px"
            borderRadius="8px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Flex flexDir="column" gap="16px">
              <Text fontSize="16px" fontWeight={600}>
                Productos
              </Text>

              <Flex flexDir="column" gap="16px">
                {fields.map((f, idx) => (
                  <RequestProductElement
                    key={f.id}
                    control={control}
                    idx={idx}
                    f={f}
                    productItems={productItems}
                    loadingProducts={loadingProducts}
                    fields={fields}
                    remove={remove}
                    disabled={loadingCreate}
                  />
                ))}
              </Flex>

              <Flex>
                <Button
                  height="36px"
                  paddingInline="14px"
                  borderRadius="8px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  disabled={
                    loadingCreate ||
                    hasIncompleteProducts ||
                    hasDuplicatedProducts
                  }
                  onClick={() => append({ idProducto: [], cantidad: "" })}
                >
                  <Plus />
                  Guardar y agregar otro producto
                </Button>
              </Flex>

              {hasDuplicatedProducts && (
                <Text fontSize="14px" color={AppColors.REQUIRED}>
                  No se pueden repetir productos.
                </Text>
              )}
            </Flex>
          </Card.Body>
        </Card.Root>

        <Card.Root maxW="full" borderRadius="8px">
          <Card.Body
            paddingX="48px"
            paddingY="30px"
            borderRadius="8px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Flex flexDir="column" gap="8px">
              <Text fontSize="16px" fontWeight={600}>
                Detalle
              </Text>
              <TextArea
                register={register as any}
                errors={errors as any}
                inputName="observaciones"
                placeholder=""
                maxLength={100}
                helperText="Máximo 100 caracteres"
                disabled={loadingCreate}
              />
            </Flex>
          </Card.Body>
        </Card.Root>
      </Flex>
    </Flex>
  );
};
